// Thêm nhanh sản phẩm mới vào kho ngay lúc tạo đơn — chỉ các trường đơn cần (tên, giá, tồn kho, cân
// nặng). Ảnh / kích thước sửa sau ở trang Sản phẩm. Lưu xong (POST /products) thì cho luôn vào đơn.
// Truyền `product` → sửa sản phẩm đang có trong đơn (lỡ nhập sai) ngay tại form, khỏi phải thoát ra mất
// thông tin khách: lưu (PATCH /products/:id) là sửa luôn trong kho.

import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MoneyInput } from "@/components/ui/money-input"
import { ApiError, patch, post } from "@/lib/api"
import { cn } from "@/lib/utils"
import { useUnitCan } from "@/lib/unitAccess"
import type { ProductOpt } from "./ProductPickerDialog"

const UNITS = ["cái", "bộ", "hộp", "chiếc", "gói", "đôi", "kg", "chai"]
const WEIGHT_PRESETS = [200, 300, 500, 1000]

const newSku = () => `SP-${Math.random().toString(36).substring(2, 7).toUpperCase()}`
const toInt = (s: string) => Math.max(0, Math.floor(Number(s.replace(/\D/g, "")) || 0))

export function QuickProductDialog({
  open,
  onOpenChange,
  initialName,
  product,
  onCreated,
  onUpdated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Tên điền sẵn (chữ đang tìm trong bảng chọn sản phẩm) */
  initialName?: string
  /** Sửa sản phẩm này thay vì thêm mới */
  product?: ProductOpt | null
  onCreated?: (p: ProductOpt) => void
  onUpdated?: (p: ProductOpt) => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState("")
  const [sku, setSku] = useState("")
  const [unit, setUnit] = useState("cái")
  const [price, setPrice] = useState(0)
  const [cost, setCost] = useState(0)
  // Nhân viên không có quyền xem tiền: không có ô giá vốn, sửa không gửi giá vốn (giữ giá vốn chủ đã nhập)
  const { seesMoney } = useUnitCan()
  const [stock, setStock] = useState("")
  const [weight, setWeight] = useState("300")
  const [errors, setErrors] = useState<Partial<Record<"name" | "sku" | "stock" | "weight", string>>>({})
  // Sửa ô nào thì bỏ lỗi của ô đó
  const fixed = (k: keyof typeof errors) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e))

  // Mỗi lần mở: thêm → một sản phẩm mới; sửa → điền theo sản phẩm đang sửa
  useEffect(() => {
    if (!open) return
    setName(product?.name ?? initialName ?? "")
    setSku(product?.sku ?? newSku())
    setUnit(product?.unit || "cái")
    setPrice(product?.price ?? 0)
    setCost(product?.cost ?? 0)
    setStock(product ? String(product.stock) : "")
    setWeight(String(product?.weightGram || 300))
    setErrors({})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- điền lại khi mở, không theo lần tải lại danh sách
  }, [open, initialName, product?.id])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        // Sửa mà không đụng mã → giữ nguyên mã cũ (kể cả mã chữ thường nhập từ Excel)
        sku: product && sku.trim() === product.sku ? product.sku : sku.trim().toUpperCase(),
        name: name.trim(),
        price: Math.round(price),
        cost: Math.round(cost),
        stock: toInt(stock),
        unit: unit.trim() || "cái",
        weightGram: toInt(weight),
      }
      return product
        ? patch<{ product: ProductOpt }>(`/products/${product.id}`, seesMoney ? body : (({ cost: _c, ...rest }) => rest)(body))
        : post<{ product: ProductOpt }>("/products", body)
    },
    onSuccess: ({ product: saved }) => {
      // Form tạo đơn đọc tồn kho / tên từ danh sách này — thay ngay, khỏi chờ tải lại
      if (product) {
        qc.setQueryData<{ products: ProductOpt[] }>(["products-active"], (d) =>
          d ? { products: d.products.map((x) => (x.id === saved.id ? { ...x, ...saved } : x)) } : d,
        )
      }
      qc.invalidateQueries({ queryKey: ["products"] })
      qc.invalidateQueries({ queryKey: ["products-active"] })
      if (product) onUpdated?.(saved)
      else onCreated?.(saved)
      onOpenChange(false)
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "product.sku_taken") setErrors({ sku: "Mã này đã có trong kho — đổi mã khác" })
      else setErrors({})
    },
  })

  function submit(e: React.FormEvent) {
    // Dựng qua portal nhưng sự kiện React vẫn nổi lên form tạo đơn — chặn lại
    e.preventDefault()
    e.stopPropagation()
    const next: typeof errors = {}
    if (!name.trim()) next.name = "Nhập tên sản phẩm"
    if (!sku.trim()) next.sku = "Nhập mã SKU"
    if (toInt(stock) < 1) next.stock = "Tối thiểu 1 để cho vào đơn"
    const w = toInt(weight)
    if (w < 1 || w > 50_000) next.weight = "Từ 1 đến 50.000 g"
    setErrors(next)
    if (Object.keys(next).length === 0) save.mutate()
  }

  const failed = save.isError && !errors.sku ? (save.error as Error)?.message || "Không lưu được sản phẩm" : null

  return (
    <Dialog open={open} onOpenChange={(o) => !save.isPending && onOpenChange(o)}>
      <DialogContent className="gap-3 sm:max-w-md" aria-describedby={undefined}>
        <div className="pr-10">
          <DialogTitle className="text-base font-semibold">{product ? "Sửa sản phẩm" : "Thêm sản phẩm"}</DialogTitle>
        </div>

        <form onSubmit={submit} className="grid grid-cols-2 gap-x-3 gap-y-2.5" noValidate>
          <Field label="Tên sản phẩm" htmlFor="qp-name" error={errors.name} className="col-span-2">
            <Input id="qp-name" value={name} onChange={(e) => (setName(e.target.value), fixed("name"))} maxLength={200} autoComplete="off" autoFocus className="h-11 desktop:h-9" />
          </Field>
          <Field label="Giá bán" htmlFor="qp-price">
            <MoneyInput id="qp-price" value={price} onValueChange={(v) => setPrice(Math.max(0, v))} className="h-11 desktop:h-9" />
          </Field>
          {seesMoney && (
            <Field label="Giá vốn" htmlFor="qp-cost">
              <MoneyInput id="qp-cost" value={cost} onValueChange={(v) => setCost(Math.max(0, v))} className="h-11 desktop:h-9" />
            </Field>
          )}
          <Field label="Tồn kho" htmlFor="qp-stock" error={errors.stock}>
            <div className="relative">
              <Input id="qp-stock" inputMode="numeric" value={stock} onChange={(e) => (setStock(e.target.value.replace(/\D/g, "").slice(0, 7)), fixed("stock"))} className="h-11 pr-12 desktop:h-9" />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">{unit || "cái"}</span>
            </div>
          </Field>
          <Field label="Cân đóng gói (g)" htmlFor="qp-weight" error={errors.weight}>
            <Input id="qp-weight" inputMode="numeric" list="qp-weight-presets" value={weight} onChange={(e) => (setWeight(e.target.value.replace(/\D/g, "").slice(0, 5)), fixed("weight"))} className="h-11 desktop:h-9" />
            <datalist id="qp-weight-presets">
              {WEIGHT_PRESETS.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </Field>
          <Field label="Mã SKU" htmlFor="qp-sku" error={errors.sku}>
            <Input id="qp-sku" value={sku} onChange={(e) => (setSku(e.target.value), fixed("sku"))} maxLength={50} autoComplete="off" className="h-11 font-mono uppercase desktop:h-9" />
          </Field>
          <Field label="Đơn vị" htmlFor="qp-unit">
            <Input id="qp-unit" value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={20} list="qp-units" autoComplete="off" className="h-11 desktop:h-9" />
            <datalist id="qp-units">
              {UNITS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </Field>

          {failed && <p className="col-span-2 text-xs text-danger">{failed}</p>}

          <div className="col-span-2 mt-1 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              Hủy
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {product ? "Lưu" : "Lưu và cho vào đơn"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, htmlFor, error, className, children }: { label: string; htmlFor: string; error?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <Label htmlFor={htmlFor} className="text-xs font-medium text-inkSoft">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}
